
import React, { PureComponent } from 'react';

import { translate } from '../../../../base/i18n';
import Video from '../../../../base/media/components/Video';
import { equals } from '../../../../base/redux';
import { checkBlurSupport } from '../../../../virtual-background/functions';
import { createLocalVideoTracks } from '../../../functions';

const videoClassName = 'video-preview-video flipVideoX';

/**
 * The type of the React {@code Component} props of {@link VideoSettingsContent}.
 */
export type Props = {

  /**
   * The deviceId of the camera device currently being used.
   */
  currentCameraDeviceId: string,

  /**
   * Callback invoked to change current camera.
   */
  setVideoInputDevice: Function,

  /**
   * Invoked to obtain translated strings.
   */
  t: Function,

  /**
   * Callback invoked to toggle the settings popup visibility.
   */
  toggleVideoSettings: Function,

  /**
   * All the camera device ids currently connected.
   */
  videoDeviceIds: string[],

  /**
   * Device id to human readable label, used when a camera has no preview track to take a label from.
   */
  deviceLabels: Object,

  /**
   * Callback executed when opening the virtual background dialog.
   */
  onOpenVirtualBackground: Function,
};

/**
 * The type of the React {@code Component} state of {@link VideoSettingsContent}.
 */
type State = {

  /**
   * Currently selected camera deviceId for immediate visual feedback.
   */
  selectedDeviceId: ?string,

  /**
   * An array of all the mhTracks and eventual errors.
   */
  trackData: Object[],
};

/**
 * Implements a React {@link Component} which displays a list of video
 * previews to choose from.
 *
 * @augments Component
 */
class VideoSettingsContent extends PureComponent<Props, State> {
    _componentWasUnmounted: boolean;

    /**
   * Identifies the most recent {@code _setTracks} run so that superseded ones can dispose the cameras
   * they opened instead of leaking them.
   */
    _setTracksRunId: number = 0;

    /**
   * Initializes a new {@code VideoSettingsContent} instance.
   *
   * @param {Object} props - The read-only properties with which the new
   * instance is to be initialized.
   */
    constructor(props) {
        super(props);

        this.state = {
            selectedDeviceId: props.currentCameraDeviceId,
            trackData: new Array(props.videoDeviceIds.length).fill({
                mhTrack: null
            })
        };
    }

    /**
   * Creates and updates the track data.
   *
   * Each run opens every camera at once to build the thumbnails, so a run that is abandoned while its
   * `getUserMedia` calls are still in flight must dispose whatever it ends up with. It previously
   * disposed `this.state.trackData` on the way in instead - a snapshot that, for an overlapping run,
   * still holds the placeholder array the constructor built, because the first run had not called
   * `setState` yet. The first run's cameras were then never released, and the next attempt to open one of
   * them failed with the device busy. That is how a camera ends up rendered as an unusable preview.
   *
   * Overlapping runs are not hypothetical on the prejoin page: `componentDidUpdate` re-runs this whenever
   * `videoDeviceIds` changes, and the device list is refreshed as the browser resolves permissions and
   * fills in device labels - which happens while the user is sitting on prejoin, not once the conference
   * is under way.
   *
   * @returns {void}
   */
    async _setTracks() {
        const runId = ++this._setTracksRunId;

        this._disposeTracks(this.state.trackData);

        const trackData = await createLocalVideoTracks(
      this.props.videoDeviceIds,
      5000
        );

        // In case the component gets unmounted, or another run superseded this one, before the tracks are
        // created avoid a leak by not setting the state.
        if (this._componentWasUnmounted || runId !== this._setTracksRunId) {
            this._disposeTracks(trackData);
        } else {
            this.setState({
                trackData
            });
        }
    }

    /**
   * Destroys all the tracks from trackData object.
   *
   * @param {Object[]} trackData - An array of tracks that are to be disposed.
   * @returns {Promise<void>}
   */
    _disposeTracks(trackData) {
        trackData.forEach(({ mhTrack }) => {
            mhTrack && mhTrack.dispose();
        });
    }

    /**
   * Returns the click handler used when selecting the video preview.
   *
   * @param {string} deviceId - The id of the camera device.
   * @returns {Function}
   */
    _onEntryClick(deviceId) {
        return () => {
            this.setState({ selectedDeviceId: deviceId });
            this.props.setVideoInputDevice(deviceId);
            this.props.toggleVideoSettings();
        };
    }

    /**
   * Renders a preview entry.
   *
   * @param {Object} data - The track data.
   * @param {number} index - The index of the entry.
   * @returns {React$Node}
   */
    _renderPreviewEntry(data, index) {
        const { error, mhTrack, deviceId } = data;
        const { currentCameraDeviceId, deviceLabels = {}, t } = this.props;
        const activeDeviceId = this.state.selectedDeviceId || currentCameraDeviceId;
        const isSelected = deviceId === activeDeviceId;
        const key = `vp-${index}`;
        const className = 'video-preview-entry';

        const props: Object = {
            className,
            key
        };

        // Being unable to build a thumbnail says nothing about whether the camera can be used - the
        // preview opens every camera at once purely to render this list, so one failing to open here is
        // routinely just a device that cannot be held twice at the same time. The entry used to return
        // early with no `onClick` in that case, which made the camera literally unselectable: the only way
        // to switch to it was to already be on it. Selection is the function of this list; the preview is
        // the decoration. Keep the entry clickable and let `replaceVideoTrackById` - which runs after the
        // popup has closed and released these preview tracks - be the thing that decides whether the
        // camera works.
        if (isSelected) {
            props.className = `${className} video-preview-entry--selected`;
        } else {
            props.onClick = this._onEntryClick(deviceId);
        }

        // `getTrackLabel` is unavailable without a track, so fall back to the name the device list
        // already carries; without it a failed entry would be an anonymous tile the user cannot identify.
        const label = (mhTrack && mhTrack.getTrackLabel()) || deviceLabels[deviceId];

        return (
            <div { ...props }>
                <div className = 'video-preview-label'>
                    {label && (
                        <div className = 'video-preview-label-container'>
                            <div className = 'video-preview-label-text'>
                                <p>{label}</p>
                            </div>
                        </div>
                    )}
                </div>
                <div className = 'video-preview-overlay' />
                {error
                    ? <div className = 'video-preview-error'>{t(error)}</div>
                    : <Video
                        className = { videoClassName }
                        playsinline = { true }
                        videoTrack = {{ mhTrack }} />}
            </div>
        );
    }

    /**
   * Implements React's {@link Component#componentDidMount}.
   *
   * @inheritdoc
   */
    componentDidMount() {
        this._setTracks();
    }

    /**
   * Implements React's {@link Component#componentWillUnmount}.
   *
   * @inheritdoc
   */
    componentWillUnmount() {
        this._componentWasUnmounted = true;
        this._disposeTracks(this.state.trackData);
    }

    /**
   * Implements React's {@link Component#componentDidUpdate}.
   *
   * @inheritdoc
   */
    componentDidUpdate(prevProps) {
        if (!equals(this.props.videoDeviceIds, prevProps.videoDeviceIds)) {
            this._setTracks();
        }
        if (this.props.currentCameraDeviceId !== prevProps.currentCameraDeviceId) {
            this.setState({ selectedDeviceId: this.props.currentCameraDeviceId });
        }
    }

    /**
   * Implements React's {@link Component#render}.
   *
   * @inheritdoc
   */
    render() {
        const { trackData } = this.state;
        const { t, onOpenVirtualBackground } = this.props;

        return (
            <div className = 'video-preview-container'>
                <div className = 'video-preview'>
                    {trackData.map((data, i) => this._renderPreviewEntry(data, i))}
                    {checkBlurSupport() && (
                        <div
                            className = 'video-preview-virtual-background-button'
                            onClick = { onOpenVirtualBackground }>
                            { t('toolbar.accessibilityLabel.preview') }
                        </div>
                    )}
                </div>

            </div>
        );
    }
}

export default translate(VideoSettingsContent);
